import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  procurementImportsApi,
  type BulkContractOutcome,
  type ContractProposal,
  type ProposalSource,
  type SkippedProposal,
} from "../../api/procurementImports";
import { PanelLoading, Spinner } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { Select } from "../../components/Select";
import { CompanyCombobox } from "../../components/CompanyCombobox";
import { procurementSuppliersApi } from "../../api/procurementContracts";
import { useFilteredQuery } from "../../lib/useFilteredQuery";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import type { ApiError } from "../../api/client";

/**
 * Predlozi otkupnih ugovora pročitani iz klijentovog registra, i dve radnje nad njima.
 *
 * Zašto ovaj ekran postoji: registar je tabela u kojoj uslovi svakog dogovora stoje u jednoj ćeliji
 * slobodnog teksta, pored imena dobavljača. Uvoz to pročita, ali ne može da završi — ime nosi i mesto
 * utovara („Axereal Nijemci" je jedan dobavljač i jedno mesto), pa upareno sa registrom od 13.761 firme
 * pada na tačno jednu firmu za 53 od 171 reda, na dve do pet za 73, a ni na jednu za 45.
 *
 * Zato ekran pokazuje **ceo tekst iz izvora** uz svako polje koje je uvoz pročitao, a odluku donosi
 * čovek. Prazno polje se vidi kao prazno; ništa se ne popunjava pretpostavkom.
 */
/**
 * Koja je firma izabrana na ovom redu.
 *
 * Jedan kandidat se nudi unaprijed izabran, jer je to najčešći slučaj i ušteda klika; sve ostalo čeka
 * izbor. Funkcija stoji izvan komponente da je dugme i potvrda čitaju **isto** — dok su to bila dva
 * izraza, dugme je moglo da bude aktivno za vrednost koju potvrda ne bi poslala.
 */
function izabrano(p: ContractProposal, izbor: Record<string, string>): string {
  const rucno = izbor[p.id];
  if (rucno !== undefined) return rucno;
  return p.candidates.length === 1 ? p.candidates[0].companyId : "";
}

/**
 * Presek pre skupnog pravljenja ugovora, i ishod posle — jedan panel, dve faze.
 *
 * Dve faze stoje u istom panelu zato što su to isti brojevi nad istim redovima: pre pokretanja
 * `napravljeno` kaže koliko bi ugovora nastalo, posle kaže koliko ih je nastalo, a `preskoceno`
 * oba puta imenuje redove koji ne mogu. Da je ishod bio poruka u uglu, sto tri reda bez tonaže
 * bi se izgubila sa njom — a njih treba naći u izvoru i dopisati im tonažu.
 *
 * Panel se ne zatvara sam. Posao traje, i čitalac mora da zatvori ishod sam, posle čitanja.
 */
function SkupnoPravljenje({
  presek,
  ishod,
  pravim,
  mozeDoUgovora,
  naPokreni,
  naZatvori,
}: {
  presek: BulkContractOutcome | null;
  ishod: BulkContractOutcome | null;
  pravim: boolean;
  mozeDoUgovora: boolean;
  naPokreni: () => void;
  naZatvori: () => void;
}) {
  const { t } = useTranslation();
  // Ishod pobeđuje presek: kad posao prođe, brojevi na ekranu su ono što je STVARNO nastalo, a ne
  // ono što se procenjivalo. Dok posla nije bilo, prikazuje se presek.
  const prikaz = ishod ?? presek;
  if (!prikaz) return null;
  const zavrseno = ishod !== null;

  /** Zašto red ne može. Dve šifre su poznate i imaju rečenicu; sve ostalo je greška sa servera. */
  const razlogTekst = (razlog: string) =>
    razlog === "NEMA_KOLICINU"
      ? t("procurementImports.bulk.reason.noQuantity", "nema upisanu tonažu")
      : razlog === "NEMA_FIRMU"
        ? t("procurementImports.bulk.reason.noCompany", "nema izabranu firmu")
        : t("procurementImports.bulk.reason.error", "greška: {{code}}", { code: razlog });

  // Redovi se grupišu po razlogu, jer je razlog ono što čovek radi dalje: tonaža se dopisuje u
  // izvoru, firma se bira na ovom ekranu. Spisak unutar grupe ostaje CEO i samo se skroluje —
  // odsečen spisak preskočenih redova je isti kvar kao da ih nema.
  const grupe = new Map<string, SkippedProposal[]>();
  for (const r of prikaz.preskoceno) {
    const lista = grupe.get(r.razlog);
    if (lista) lista.push(r);
    else grupe.set(r.razlog, [r]);
  }

  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h3>
            {zavrseno
              ? t("procurementImports.bulk.doneTitle", "Ugovori su napravljeni")
              : t("procurementImports.bulk.previewTitle", "Pre pokretanja")}
          </h3>
          <div className="sub">
            {t(
              "procurementImports.bulk.draftsOnly",
              "Ugovori nastaju kao nacrti. Nacrt aktivira direktor, skupno pravljenje ne aktivira ništa.",
            )}
          </div>
        </div>
        <button type="button" className="btn ghost sm" disabled={pravim} onClick={naZatvori}>
          {t("common.close", "Zatvori")}
        </button>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span className="tag ok">
            {zavrseno
              ? t("procurementImports.bulk.made", "napravljeno {{n}}", { n: prikaz.napravljeno })
              : t("procurementImports.bulk.willMake", "nastaje {{n}} ugovora", {
                  n: prikaz.napravljeno,
                })}
          </span>
          {prikaz.preskoceno.length > 0 && (
            <span className="tag warn">
              {t("procurementImports.bulk.skipped", "preskočeno {{n}}", {
                n: prikaz.preskoceno.length,
              })}
            </span>
          )}
        </div>

        {prikaz.napravljeno === 0 && prikaz.preskoceno.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
            {t(
              "procurementImports.bulk.nothingMapped",
              "Nema uparenih predloga. Prvo izaberite firmu na redovima koji čekaju.",
            )}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
            {zavrseno
              ? t(
                  "procurementImports.bulk.doneLead",
                  "Preskočeni redovi ostaju u predlozima, upareni i nedirnuti. Ponovno pokretanje nastavlja odande, a ne iz početka.",
                )
              : t(
                  "procurementImports.bulk.previewLead",
                  "Svaki red ide u svojoj transakciji, pa pad jednog ne obara ostale. Red koji je već postao ugovor se preskače.",
                )}
          </p>
        )}

        {[...grupe.entries()].map(([razlog, redovi]) => (
          <div key={razlog} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
              <b>{razlogTekst(razlog)}</b>{" "}
              <span style={{ color: "var(--ink-3)" }}>
                — {t("common.rowCount", "{{n}} redova", { n: redovi.length })}
              </span>
            </div>
            {/* Spisak je ceo, pa se skroluje: sto tri reda bez tonaže ne smeju biti odsečena na
                „prvih deset", jer se svaki od njih traži u izvoru pojedinačno. */}
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                background: "var(--surface-2)",
                borderRadius: 6,
                padding: "6px 8px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {redovi.map((r) => (
                <div
                  key={r.proposalId}
                  style={{ fontSize: 12, color: "var(--ink-2)", overflowWrap: "anywhere" }}
                >
                  <span style={{ color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>
                    {t("procurementImports.row", "red {{n}}", { n: r.sourceRow })}
                  </span>{" "}
                  · {r.supplierName}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 8 }}>
          {zavrseno ? (
            <>
              {mozeDoUgovora && prikaz.napravljeno > 0 && (
                <Link to="/procurement/contracts" className="btn" style={{ textDecoration: "none" }}>
                  {t("procurementImports.bulk.openRegister", "Otvori registar ugovora")}
                </Link>
              )}
              <button type="button" className="btn primary" onClick={naZatvori}>
                {t("common.close", "Zatvori")}
              </button>
            </>
          ) : prikaz.napravljeno === 0 && prikaz.preskoceno.length === 0 ? (
            /* Nema šta da se pravi. Dugme „Napravi 0 ugovora" nije kontrola nego šum — jedini posao
               koji ovde preostaje je zatvoriti panel i upariti redove. */
            <button type="button" className="btn primary" onClick={naZatvori}>
              {t("common.close", "Zatvori")}
            </button>
          ) : (
            <>
              <button type="button" className="btn" disabled={pravim} onClick={naZatvori}>
                {t("common.cancel", "Odustani")}
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={pravim || prikaz.napravljeno === 0}
                onClick={naPokreni}
              >
                {pravim && <Spinner size={14} />}
                {pravim
                  ? t("procurementImports.bulk.working", "Pravim ugovore…")
                  : t("procurementImports.bulk.run", "Napravi {{n}} ugovora", {
                      n: prikaz.napravljeno,
                    })}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ProcurementImportsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  // `MAPPED` je stanje kao i svako drugo i mora da stoji u tipu: izbor „Upareni" ga postavlja, a
  // dok ga tip nije priznavao prolazilo je samo kroz prisilno kastovanje — provera je ćutala o
  // vrednosti koju kod stvarno postavlja.
  const [stanje, setStanje] = useState<"PENDING" | "MAPPED" | "CONFIRMED" | "REJECTED">("PENDING");
  const [redovi, setRedovi] = useState<ContractProposal[] | null>(null);
  const [zbir, setZbir] = useState<Record<string, number>>({});
  const [izbor, setIzbor] = useState<Record<string, string>>({});
  const [radi, setRadi] = useState<string | null>(null);
  // Razlog se kuca u redu, ne u iskacucem prozoru: covek koji odbija red treba da vidi tekst predloga
  // dok pise zasto to nije ugovor.
  const [otvorenRazlog, setOtvorenRazlog] = useState<Record<string, boolean>>({});
  const [razlozi, setRazlozi] = useState<Record<string, string>>({});
  // Čitanje u toku. Drži se odvojeno od `redovi === null` jer to dvoje nije isto: prazno pri prvom
  // otvaranju znači „nema još ničega" i tu ide kostur, a pri prelasku na drugu stranu redovi već
  // postoje i treba da ostanu na ekranu — inače spisak pri svakom kliku na „Sledeća" trepne u kostur
  // i čitalac izgubi mesto na kom je bio.
  const [ucitava, setUcitava] = useState(false);
  // Dobavljači se traže NA SERVERU dok se kuca. Ranije je ceo spisak putovao do pregledača — 2.113
  // redova pri svakom otvaranju ekrana — pa se ovde sekao na pedeset; ko je tražio firmu, kucao je u
  // spisak koji je već bio odsečen.
  //
  // Kandidat je prečica a ne granica: uparivanje po imenu pogreši, a za 75 od 284 reda ne nađe ništa.
  const [pretraga, setPretraga] = useState("");
  // Strana i izvor. Spisak se ne povlači ceo: posle uvoza iz registra ugovora starog ERP-a ima 692
  // predloga, a svaki rod ih donosi još. Izvor je filter jer isti dogovor može doći iz oba.
  const [strana, setStrana] = useState(0);
  const [ukupno, setUkupno] = useState(0);
  const [izvor, setIzvor] = useState<ProposalSource>("ALL");
  // Skupno pravljenje ugovora. Tri stanja, i sva tri su zasebna: presek je ono što bi nastalo,
  // ishod je ono što je nastalo, a `pravim` je posao u toku — traje, jer je redova petsto osamdeset.
  // Da je ishod živeo u istoj promenljivoj kao presek, panel ne bi mogao da razlikuje „nastaje 580"
  // od „napravljeno 580", a to su dve različite tvrdnje.
  const [presek, setPresek] = useState<BulkContractOutcome | null>(null);
  const [ishod, setIshod] = useState<BulkContractOutcome | null>(null);
  const [trazimPresek, setTrazimPresek] = useState(false);
  const [pravim, setPravim] = useState(false);
  const VELICINA = 25;
  const dobavljaci = useFilteredQuery((tekst) => procurementSuppliersApi.search(tekst), {
    keys: [],
    text: pretraga,
  });

  const mozeResiti = hasPermission("procurement_imports:resolve");
  const mozeDoUgovora = hasPermission("section:procurement_contracts:read");

  // `toast` i `t` se drže u ref-u, a ne u zavisnostima: oba su nov objekat pri svakom crtanju, pa bi
  // efekt koji ih ima u zavisnostima pokretao čitanje u krug — učitaj, nacrtaj, učitaj. Isti kvar koji je
  // na spisku finansija terao kostur da treperi.
  const toastRef = useRef(toast);
  const tRef = useRef(t);
  toastRef.current = toast;
  tRef.current = t;

  const ucitaj = useCallback(async () => {
    setUcitava(true);
    try {
      const [r, z] = await Promise.all([
        procurementImportsApi.list(stanje, izvor, strana, VELICINA),
        procurementImportsApi.tally(),
      ]);
      setRedovi(r.content);
      setUkupno(r.totalElements);
      setZbir(z);
    } catch (e) {
      toastRef.current.error(
        (e as ApiError)?.message ?? tRef.current("common.loadFailed", "Čitanje nije prošlo."),
      );
      setRedovi([]);
    } finally {
      setUcitava(false);
    }
  }, [stanje, izvor, strana]);

  useEffect(() => {
    void ucitaj();
  }, [ucitaj]);

  /**
   * Upari predlog sa firmom. Ugovor se NE pravi — to je skupni korak, posle.
   *
   * Upisuje se odmah po izboru: ovo je radni ekran kroz koji otkup prolazi 692 reda, i svaki dodatni
   * klik na „sačuvaj" je 692 klika.
   */
  async function upari(p: ContractProposal, companyId: string) {
    // Izbor se upisuje pre nego što server potvrdi, pa pad mora da vrati red u zatečeno stanje.
    // Bez toga red izgleda uparen sve do osvežavanja, a u bazi ga nema — što je gore od poruke o
    // grešci, jer čovek koji prolazi kroz 692 reda nema razloga da osvežava svaki.
    const bilo = izbor[p.id];
    setIzbor((s) => ({ ...s, [p.id]: companyId }));
    setRadi(p.id);
    try {
      await procurementImportsApi.map(p.id, companyId);
      await ucitaj();
    } catch (e) {
      setIzbor((s) => {
        const sledece = { ...s };
        if (bilo === undefined) delete sledece[p.id];
        else sledece[p.id] = bilo;
        return sledece;
      });
      toast.error((e as ApiError)?.message ?? t("common.saveFailed", "Čuvanje nije prošlo."));
    } finally {
      setRadi(null);
    }
  }

  /** Skini uparivanje — čovek koji se predomisli ne sme da ostane zaključan. */
  async function skiniUparivanje(p: ContractProposal) {
    setRadi(p.id);
    try {
      await procurementImportsApi.unmap(p.id);
      setIzbor((s) => {
        const sledece = { ...s };
        delete sledece[p.id];
        return sledece;
      });
      await ucitaj();
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("common.saveFailed", "Čuvanje nije prošlo."));
    } finally {
      setRadi(null);
    }
  }

  /**
   * Traži presek pre skupnog pravljenja ugovora. NE pravi ništa.
   *
   * Dugme ne pokreće odmah zato što radnja nije ono što se poništava jednim klikom: posle nje u
   * registru stoji petsto osamdeset nacrta. Presek kaže koliko bi ih nastalo i koji redovi ne mogu,
   * pa čovek odluči nad brojem a ne nad pretpostavkom.
   */
  async function traziPresek() {
    setIshod(null);
    setTrazimPresek(true);
    try {
      setPresek(await procurementImportsApi.mappedPreview());
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("common.loadFailed", "Čitanje nije prošlo."));
    } finally {
      setTrazimPresek(false);
    }
  }

  /**
   * Napravi ugovore od svih uparenih predloga, odjednom.
   *
   * Posle posla se čita i spisak i zbir po stanjima: pet stotina osamdeset redova je prešlo iz
   * `MAPPED` u `CONFIRMED`, pa brojevi u biraču stanja koji se ne osveže lažu o istom ekranu.
   * Ishod se drži na ekranu i posle osvežavanja — preskočeni redovi se traže u izvoru, i da su
   * nestali sa porukom, njihov spisak bi se izgubio.
   */
  async function pokreniSkupno() {
    setPravim(true);
    try {
      const i = await procurementImportsApi.confirmMapped();
      setIshod(i);
      setPresek(null);
      if (i.preskoceno.length === 0) {
        toast.success(
          t("procurementImports.bulk.allMade", "Napravljeno je {{n}} nacrta ugovora.", {
            n: i.napravljeno,
          }),
        );
      }
      // Strana na kojoj je čitalac stajao može sada da bude IZA kraja: od 683 uparena reda ostaje
      // 103, pa je spisak od dvadeset osam strana pao na pet. Strana iza kraja vraća prazno, a
      // prazno se na ovom ekranu čita kao „nema redova u ovom stanju" — što nije tačno. Zato se
      // vraća na prvu stranu, a efekt koji prati stranu sam ponovo pročita spisak i zbir, pa se
      // ne čita dvaput.
      if (strana !== 0) setStrana(0);
      else await ucitaj();
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("common.saveFailed", "Čuvanje nije prošlo."));
    } finally {
      setPravim(false);
    }
  }

  async function odbaci(p: ContractProposal) {
    const razlog = (razlozi[p.id] ?? "").trim();
    if (!razlog) return;
    setRadi(p.id);
    try {
      await procurementImportsApi.reject(p.id, razlog);
      toast.success(t("procurementImports.rejected", "Red je ostavljen po strani."));
      setOtvorenRazlog((s) => ({ ...s, [p.id]: false }));
      await ucitaj();
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("common.saveFailed", "Čuvanje nije prošlo."));
    } finally {
      setRadi(null);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("procurementImports.title", "Predlozi otkupnih ugovora")}</h1>
          <div className="sub">
            {t(
              "procurementImports.lead",
              "Radni ekran za uparivanje dobavljača. Pročitano iz dva izvora: evidencije otkupa soje i registra zaključenih ugovora — uslovi u oba stoje kao slobodan tekst pored imena, pa je mašina pročitala sve osim toga KOJA JE FIRMA. Vaš posao je samo to: izaberite dobavljača iz padajućeg menija, ili recite da red nije ugovor i zašto. Ugovori se prave skupno, kad uparivanje bude gotovo. Isti dogovor može doći iz oba izvora — oznaka pored reda kaže iz kog je.",
            )}
          </div>
        </div>
        <div className="page-actions">
          <Select
            value={izvor}
            onChange={(v) => {
              setIzvor(v as ProposalSource);
              setStrana(0);
            }}
            options={[
              { value: "ALL", label: t("procurementImports.sourceAll", "oba izvora") },
              { value: "ERP", label: t("procurementImports.sourceErp", "registar ugovora") },
              { value: "XLS", label: t("procurementImports.sourceXls", "evidencija otkupa") },
            ]}
          />
          <Select
            value={stanje}
            onChange={(v) => {
              setStanje(v as typeof stanje);
              setStrana(0);
            }}
            options={[
              { value: "PENDING", label: t("procurementImports.pending", "Čekaju ({{n}})", { n: zbir.PENDING ?? 0 }) },
              { value: "MAPPED", label: t("procurementImports.mappedTab", "Upareni ({{n}})", { n: zbir.MAPPED ?? 0 }) },
              { value: "CONFIRMED", label: t("procurementImports.confirmedTab", "Potvrđeni ({{n}})", { n: zbir.CONFIRMED ?? 0 }) },
              { value: "REJECTED", label: t("procurementImports.rejectedTab", "Po strani ({{n}})", { n: zbir.REJECTED ?? 0 }) },
            ]}
          />
          {/* Skupno pravljenje ugovora — zadnji korak ovog ekrana, i jedini koji ne radi red po red.
              Stoji pored birača stanja jer se tiče jednog stanja: uparenih. Broj je serverov zbir, ne
              broj redova na strani, pa kaže koliko ih čeka a ne koliko ih se vidi. */}
          {mozeResiti && (
            <button
              type="button"
              className="btn primary"
              disabled={trazimPresek || pravim}
              onClick={() => void traziPresek()}
            >
              {trazimPresek && <Spinner size={14} />}
              {/* Dok posao traje, naznaka „radi" stoji na dugmetu u panelu — na onom koje je čovek
                  pritisnuo. Ovo ostaje onemogućeno i pri svom imenu: dva dugmeta sa istim tekstom
                  „Pravim ugovore…" ne kažu koje od njih radi. */}
              {trazimPresek
                ? t("procurementImports.bulk.checking", "Računam presek…")
                : t("procurementImports.bulk.start", "Napravi ugovore iz uparenih ({{n}})", {
                    n: zbir.MAPPED ?? 0,
                  })}
            </button>
          )}
        </div>
      </div>

      <SkupnoPravljenje
        presek={presek}
        ishod={ishod}
        pravim={pravim}
        mozeDoUgovora={mozeDoUgovora}
        naPokreni={() => void pokreniSkupno()}
        naZatvori={() => {
          setPresek(null);
          setIshod(null);
        }}
      />

      {redovi === null ? (
        <PanelLoading text={t("procurementImports.loading", "Čitam predloge…")} />
      ) : redovi.length === 0 ? (
        <div className="empty">
          {stanje === "PENDING"
            ? t("procurementImports.nonePending", "Nema predloga koji čekaju. Uvoz ih upisuje iz evidencije otkupa.")
            : t("procurementImports.noneOther", "Nema redova u ovom stanju.")}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            // Dok sledeća strana putuje, zatečena se zatamni ali ostane čitljiva, a dugmad se ne
            // primaju dvaput. Vrteška je u straniču, pored broja strane — tamo se i klik desio.
            opacity: ucitava ? 0.55 : 1,
            pointerEvents: ucitava ? "none" : undefined,
            transition: "opacity 120ms",
          }}
        >
          {/* Stranič stoji i iznad i ispod: iznad da se vidi koliko ih ukupno ima pre čitanja, ispod da
              dugme ne ostane van vidokruga kad se prođe strana. */}
          <Pager page={strana} size={VELICINA} total={ukupno} busy={ucitava} onPage={setStrana} />
          {redovi.map((p) => (
            <div key={p.id} className="card">
              <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 8 }}>
                  <b style={{ fontSize: 15 }}>{p.supplierName}</b>
                  {/* Predmet dogovora. U evidenciji ga nijedan red ne piše jer je cela soja, pa bi bez
                      ovoga izgledao kao polje koje fali umesto kao ono što se podrazumeva. */}
                  <span className="tag neutral">{p.culture}</span>
                  <span className="tag neutral">{t("procurementImports.crop", "rod {{n}}", { n: p.harvestYear })}</span>
                  {p.quantityT != null && (
                    <span className="tag neutral">{p.quantityT} t</span>
                  )}
                  {p.pricePerT != null && (
                    <span className="tag neutral">
                      {p.pricePerT} {p.priceCurrency ?? ""} / t
                    </span>
                  )}
                  {p.parity && <span className="tag neutral">{p.parity}</span>}
                  {/* Iz kog izvora predlog dolazi. Dok je izvor bio jedan, list i red su bili dovoljni;
                      sa dva izvora „2024 · red 65" može biti i list evidencije i godina u ERP-u. */}
                  <span style={{ marginLeft: "auto", display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span className="tag neutral" style={{ fontSize: 11 }}>
                      {p.sourceFile?.startsWith("ERP:")
                        ? t("procurementImports.sourceErp", "registar ugovora")
                        : t("procurementImports.sourceXls", "evidencija otkupa")}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                      {p.sourceSheet} · {t("procurementImports.row", "red {{n}}", { n: p.sourceRow })}
                    </span>
                  </span>
                </div>

                {/* Ceo tekst iz kog je sve pročitano. Stoji uz polja, jer je on ono prema čemu se
                    proverava — a ne obrnuto. */}
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: "var(--ink-2)",
                    background: "var(--surface-2)",
                    padding: "6px 8px",
                    borderRadius: 6,
                    overflowWrap: "anywhere",
                  }}
                >
                  {p.sourceText}
                </p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12, color: "var(--ink-2)" }}>
                  {p.loadingPlace && (
                    <span>
                      {t("procurementImports.place", "Mesto")}: <b>{p.loadingPlace}</b>
                    </span>
                  )}
                  {(p.loadingNote || p.loadingTo) && (
                    <span>
                      {t("procurement.contract.loading", "Utovar")}:{" "}
                      <b>{p.loadingNote ?? p.loadingTo}</b>
                    </span>
                  )}
                  {p.paymentDays != null && (
                    <span>
                      {t("procurement.contract.payment", "Plaćanje")}: <b>{p.paymentDays}</b>{" "}
                      {p.paymentBase === "LOADING"
                        ? t("procurement.contract.payment.loading", "po utovaru")
                        : p.paymentBase === "DELIVERY"
                          ? t("procurement.contract.payment.delivery", "po isporuci")
                          : p.paymentBase === "INVOICE"
                            ? t("procurement.contract.payment.invoice", "po fakturi")
                            : ""}
                    </span>
                  )}
                </div>

                {p.status === "PENDING" ? (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                    {/* Birač ide preko CELOG registra dobavljača, a kandidati su dugmad-prečice iznad
                        njega. Tako red bez kandidata nije ćorsokak, a pogrešno uparen se može ispraviti.

                        Oboje stoji iza prava na rešavanje. Direktor prodaje čita otkupnu stranu a na
                        njoj ništa ne menja (pravilo 2), pa ima `:view` a nema `:resolve` — i dok mu je
                        birač stajao na ekranu, svaki izbor je vraćao 403. Kontrola koja ne može da
                        uspe gora je od kontrole koje nema: čitalac ne vidi da je odbijen, nego da se
                        ne čuva. Kandidati mu se i dalje ČITAJU, jer je čitanje posao koji sme. */}
                    {mozeResiti ? (
                      <>
                        <CompanyCombobox
                          companies={dobavljaci.data?.rows ?? []}
                          totalMatches={dobavljaci.data?.totalAtMost}
                          onQueryChange={setPretraga}
                          value={izabrano(p, izbor)}
                          onChange={(v) => {
                            if (v) void upari(p, v);
                          }}
                          placeholder={t("procurementImports.choose", "Izaberite dobavljača")}
                        />
                        {p.candidates.map((c) => (
                          <button
                            key={c.companyId}
                            type="button"
                            className={izabrano(p, izbor) === c.companyId ? "tag ok" : "tag neutral"}
                            style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
                            onClick={() => void upari(p, c.companyId)}
                          >
                            {c.companyName}
                          </button>
                        ))}
                      </>
                    ) : (
                      p.candidates.map((c) => (
                        <span key={c.companyId} className="tag neutral" style={{ fontSize: 11 }}>
                          {c.companyName}
                        </span>
                      ))
                    )}
                    {p.candidates.length === 0 && mozeResiti && (
                      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {t(
                          "procurementImports.noCandidate",
                          "Uparivanje po imenu nije našlo firmu — potražite je u biraču, ili je prvo zavedite.",
                        )}
                      </span>
                    )}
                    {mozeResiti && (
                      <button
                        type="button"
                        className="btn sm"
                        disabled={radi === p.id}
                        onClick={() => setOtvorenRazlog((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      >
                        {t("procurementImports.reject", "Nije ugovor")}
                      </button>
                    )}
                    {mozeResiti && otvorenRazlog[p.id] && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", marginTop: 6 }}>
                        <input
                          className="input"
                          autoFocus
                          value={razlozi[p.id] ?? ""}
                          placeholder={t("procurementImports.rejectReason", "Zašto ovaj red nije ugovor?")}
                          onChange={(e) => setRazlozi((s) => ({ ...s, [p.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void odbaci(p);
                            if (e.key === "Escape") setOtvorenRazlog((s) => ({ ...s, [p.id]: false }));
                          }}
                          style={{ flex: 1, minWidth: 200 }}
                        />
                        <button
                          type="button"
                          className="btn primary sm"
                          disabled={radi === p.id || !(razlozi[p.id] ?? "").trim()}
                          onClick={() => void odbaci(p)}
                        >
                          {t("procurementImports.rejectSave", "Ostavi po strani")}
                        </button>
                      </div>
                    )}
                  </div>
                ) : p.status === "MAPPED" ? (
                  /* Upareno: firma je izabrana, ugovor se pravi skupno kad uparivanje bude gotovo. */
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <span className="tag ok">{p.resolvedCompanyName ?? "—"}</span>
                    {/* Tonaža je na ugovoru obavezna i iz nje se računa realizacija, pa uparen red bez
                        nje ne može da postane ugovor — 103 od 683. Oznaka stoji na redu, ne samo u
                        ishodu: red koji će biti preskočen treba da se vidi PRE pokretanja, da mu se
                        tonaža dopiše u izvoru. */}
                    {p.quantityT == null && (
                      <span
                        className="tag warn"
                        title={t(
                          "procurementImports.noQuantityHint",
                          "Bez tonaže ugovor ne može da nastane. Dopišite je u izvoru.",
                        )}
                      >
                        {t("procurementImports.noQuantity", "fali tonaža")}
                      </span>
                    )}
                    <span style={{ color: "var(--ink-3)" }}>
                      {t("procurementImports.mapped", "Upareno — ugovor se pravi skupno, kad uparivanje bude gotovo.")}
                    </span>
                    {mozeResiti && (
                      <button
                        type="button"
                        className="btn sm"
                        disabled={radi === p.id}
                        onClick={() => void skiniUparivanje(p)}
                      >
                        {t("procurementImports.unmap", "Promeni firmu")}
                      </button>
                    )}
                  </div>
                ) : p.status === "CONFIRMED" ? (
                  <div style={{ fontSize: 12 }}>
                    <Link to={`/procurement/contracts/${p.createdContractId}`}>
                      {t("procurementImports.openContract", "Otvori nacrt ugovora")} →
                    </Link>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    {t("procurementImports.setAside", "Po strani")}: {p.rejectedReason}
                  </div>
                )}
              </div>
            </div>
          ))}
          <Pager page={strana} size={VELICINA} total={ukupno} busy={ucitava} onPage={setStrana} />
        </div>
      )}
    </div>
  );
}
